/**
 * ============================================================================
 * ⭐⭐ WHAT THE CHECK-IN PANEL IS ABOUT — DRIVERS, CLIENTS OR CARRIERS
 * ============================================================================
 *
 * > Pete, 2026-09-19: *"The driver checkin panel needs a toggle between Drivers,
 * > Clients and Carriers. All the same functionality as a driver."*
 *
 * Decision 188, §1AV.171. This module owns three things and nothing else: the
 * subject type, the WORDS for each subject, and where the choice is remembered.
 *
 * ---- ⛔⛔ ONE MAP, BECAUSE THERE ARE TWO LABELS PER SUBJECT ------------------
 *
 * Each subject has a CHIP label and a PANEL TITLE, and for drivers they differ:
 *
 *   | subject  | chip       | title               |
 *   |----------|------------|---------------------|
 *   | driver   | `Drivers`  | `Driver Check-Ins`  |
 *   | client   | `Clients`  | `Clients`           |
 *   | carrier  | `Carriers` | `Carriers`          |
 *
 * ⭐ Pete asked for exactly that: *"Leave the chips as Drivers not Driver
 * Check-Ins"* … *"Driver Check-in will still be in the title though, just not the
 * chips"*. ⚠️ AND IT IS DELIBERATE, NOT AN OVERSIGHT TO TIDY LATER. The chips are
 * a SET and have to scan as three parallel nouns; the title is a LANDMARK, and
 * "Driver Check-Ins" is the phrase the people who live in this panel look for.
 *
 *   ** A LABEL IN A SET OF THREE AND A LABEL STANDING ALONE ARE NOT DOING THE
 *      SAME JOB, AND MAKING THEM IDENTICAL SERVES THE DIAGRAM RATHER THAN
 *      EITHER READER. **
 *
 * ⛔ SO BOTH STRINGS FOR A SUBJECT LIVE ON ONE ROW OF ONE MAP. Two literals in
 * two files that must stay in step is precisely how they stop being in step —
 * §1AV.170 spent a day and a half on exactly that failure.
 *
 * ---- ⚠️ "Today" IS NOT IN ANY TITLE, ON PURPOSE -----------------------------
 *
 * ⛔ My draft said `Clients Today`. Pete cut it: *"just have it say Clients"*.
 * The panel only ever shows one day and the date is in the toolbar directly
 * above it.
 *   ** A WORD THAT IS TRUE OF EVERY STATE OF A SCREEN CARRIES NO INFORMATION IN
 *      ANY OF THEM. **
 * ============================================================================
 */

export type PanelSubject = 'driver' | 'client' | 'carrier' | 'consignee';

/**
 * ---- ⛔⚤ ~~AN `orders` SUBJECT~~ — PROPOSED, BUILT, AND DROPPED (§0.3) --------
 *
 * ⭐ Pete asked for *"Orders In and Orders Out"* chips, then *"change the tick to
 * 'Orders' then in the search we have checkboxes for inbound and outbound"*, then
 * looked at his own screen and withdrew it:
 *
 * > *"This will be redundant with the WMS Orders Panel on the right. I think today,
 * > when we select a client or carrier in the Driver Checkin panel, it automatically
 * > pulls the WMS orders in the right panel correct?"*
 *
 * ✅ **MEASURED, AND THE ANSWER WAS NO — WHICH IS WHY THE CHIP LOOKED NECESSARY.**
 * `clientFilter` / `carrierFilter` (`CheckInPanel.tsx` L1248-49) were used in exactly
 * two places: the `visible` memo (L1863-66) and the "filtered" badge (L1824). A pure
 * LOCAL LIST FILTER. The rail focus had one source and it was hover dwell.
 *
 *   ** A MISSING BEHAVIOUR GETS PROPOSED AS A NEW FEATURE. THE FEATURE LOOKS
 *      REDUNDANT WITH SOMETHING THAT ALREADY EXISTS, AND THE REDUNDANCY IS THE
 *      CLUE — NOT TO DROP THE FEATURE, BUT THAT THE WIRING IS ABSENT. **
 *
 * ⛔ SO THE `orders` SUBJECT IS GONE and the rail wiring replaces it: selecting a
 * client or carrier sets a party `RailFocus`, and the WMS Orders panel — with its
 * own Inbound/Outbound, source and status filters, all richer than a chip — follows.
 * ⚠️ The `SubjectKind` type and the `directions` flag went with it; three subjects
 * are all parties, so a kind discriminator would have had one value.
 */

/** ⛔ The render order of the chips. Not alphabetical — drivers first because it
 *  is the default and the one used most. */
export const PANEL_SUBJECTS: readonly PanelSubject[] =
    ['driver', 'client', 'carrier', 'consignee'] as const;

interface SubjectWords {
    /** The chip. Short — five of these share one narrow row. */
    chip: string;
    /** The panel heading. May differ from the chip — see the note above. */
    title: string;
    /** Which of the search dropdowns this subject shows. */
    dropdowns: { client: boolean; carrier: boolean; consignee: boolean };
}

/**
 * ⛔⛔ THE ONE MAP. Nothing else in the codebase may spell these words.
 * A guard asserts that no component hard-codes `'Driver Check-Ins'`.
 */
export const SUBJECT_WORDS: Readonly<Record<PanelSubject, SubjectWords>> = {
    /**
     * ---- ⛔⛔ THE CHIPS ARE SINGULAR AND THE TITLES ARE PLURAL (2026-09-19) ----
     *
     * ⭐ Pete chose this to stop `Consignees` wrapping in a 340px panel: four
     * singular words plus the three icons fit on one line; four plural ones do not.
     *
     * ⛔⤴ ~~`Drivers` / `Clients` / `Carriers` / `Consignees`~~ as chips — struck
     * (§0.3). ⚠️ His earlier *"Leave the chips as Drivers not Driver Check-Ins"* was
     * about the chip not carrying the TITLE's words; it was never a ruling on plural
     * vs singular, and this does not reverse it.
     *
     * ⭐⭐ AND THE TWO FIELDS DISAGREEING IS THE POINT OF HAVING TWO FIELDS. A chip is
     * a thing you pick — *"show me Client"* — so it names the KIND. A heading
     * describes what is under it — a LIST of clients — so it stays plural. `driver`
     * already needed this split (`Driver` vs `Driver Check-Ins`); the other three
     * turn out to need it too.
     *
     *   ** A LABEL YOU CHOOSE AND A LABEL THAT DESCRIBES WHAT YOU CHOSE ARE NOT THE
     *      SAME WORD, AND A NARROW ROW IS WHERE THAT STOPS BEING PEDANTRY. **
     *
     * ⛔ WIDTH IS A REQUIREMENT HERE, NOT A PREFERENCE. This row shares its line with
     * `[columns] [search] [⚠ n]`, whose order is load-bearing (CheckInPanel.tsx).
     */
    driver:    { chip: 'Driver',    title: 'Driver Check-Ins',
                 dropdowns: { client: true,  carrier: true,  consignee: false } },
    client:    { chip: 'Client',    title: 'Clients',
                 dropdowns: { client: true,  carrier: false, consignee: false } },
    carrier:   { chip: 'Carrier',   title: 'Carriers',
                 dropdowns: { client: false, carrier: true,  consignee: false } },
    consignee: { chip: 'Consignee', title: 'Consignees',
                 dropdowns: { client: false, carrier: false, consignee: true  } },
};

/**
 * ---- ⛔⛔ THE DROPDOWN RULE IS A MAP, NOT A DERIVATION --------------------
 *
 * ⚠️ This table listed `ordersIn` / `ordersOut` until 2026-09-19 — rows for a
 * subject that was built and withdrawn the same evening. **A COMMENT THAT OUTLIVES
 * THE CODE IT DESCRIBES IS WORSE THAN NO COMMENT: IT IS CONFIDENTLY WRONG.**
 *
 * It is tempting to write `show the dropdown that is not the subject`. That is right
 * for two of the four rows and wrong for the other two:
 *
 *   driver     clients + carriers  — unchanged from today; nothing anyone relies on
 *                                    moves, and NO consignee dropdown is added here
 *   client     clients             — Pete: *"just one or the other depending on if
 *                                    the client or carrier is ticked"*, and it
 *                                    narrows the CARD LIST and the SCHEDULE to one
 *   carrier    carriers            — mirrored
 *   consignee  consignees          — same rule again (Pete, 2026-09-19)
 *
 *   ** A RULE THAT FITS HALF THE ROWS IS NOT A RULE, IT IS A COINCIDENCE WITH
 *      EXCEPTIONS — AND THE EXCEPTIONS ARE WHERE IT WILL BE READ AS A BUG. **
 *
 * ⚠️ A dropdown that is hidden must also be CLEARED. Hiding a filter that is
 * currently narrowing the list leaves a shorter list with nothing on screen
 * explaining it — the rule `hideStatuses` earned on the Blocked chip.
 *   ** NEVER HIDE A CONTROL THAT IS CURRENTLY DOING SOMETHING. **
 */

/**
 * ---- ⛔⚤ ~~`In` / `Out` COLLIDE WITH THE HEADER ABOVE~~ — MOOT (§0.3) --------
 *
 * There were briefly TWO order chips, `In` and `Out`, and they sat directly under
 * a header reading `In 1  Out 0  Trucks 1` — same words, different meaning. It was
 * recorded as an accepted trade-off. ⭐ Pete then removed the problem instead of
 * accepting it: *"change the tick to 'Orders' then in the search we have checkboxes
 * for inbound and outbound"*.
 *
 * ✅ **AND IT IS THE SAME DISTINCTION HE DREW ABOUT THE DROPDOWNS, APPLIED AGAIN:
 * the SUBJECT says what a card IS; a FILTER says which cards show.** Direction does
 * not change what an order is — it selects among orders. It was never a subject.
 *
 *   ** A CHOICE THAT IS AWKWARD TO LABEL IS OFTEN IN THE WRONG CONTROL. THE
 *      LABELLING PROBLEM IS THE SYMPTOM; THE MISPLACED CONCEPT IS THE FAULT. **
 *
 * ⚠️ So it collapses 5 chips to 4, the row stops threatening to wrap, and the
 * collision disappears rather than being mitigated.
 */

/**
 * ⛔ Per FACILITY, exactly like the door order. Brighton's scheduler and Reno's
 * do different jobs; one shared choice would be nonsense.
 */
/**
 * ---- ⭐⭐ DECISION 30 — THE TITLE NAMES WHAT IS SELECTED (2026-09-19) ----------
 *
 * ⭐ Pete: ***"To keep things consistent, when the driver is hovered/selected, the
 * Driver Check-Ins should change to Driver - Pete L. / Appt xxxxx"***
 *
 * ⛔⛔ THE TITLE IS THE PANEL'S STATEMENT OF SCOPE, AND IT HAS TO BE TRUE IN EVERY
 * STATE. §1AV.171: *"the panel title becomes a lie when the subject changes"* — and
 * it becomes a lie again the moment ONE party is selected and the heading still says
 * the plural. Decision 8 made it follow the SUBJECT; this makes it follow the
 * SELECTION when there is one.
 *
 *   ** A HEADING THAT NAMES A CATEGORY WHILE THE BODY SHOWS ONE MEMBER OF IT IS THE
 *      SAME FAULT AS A COUNT THAT DESCRIBES A DIFFERENT SET FROM THE ROWS BELOW IT. **
 *
 * ⚠️ THE DRIVER FORM IS PETE'S, VERBATIM. The three party forms are the same shape
 * applied to a party and are ⬜ NOT recorded anywhere else — stated as an
 * extrapolation in §1AV.171's build spec rather than presented as settled.
 *
 * ⛔ A PURE FUNCTION, AND THAT IS THE POINT. The title is asserted by
 * `checkin-panel-guard`; computing it inline in JSX would put the rule somewhere a
 * guard can only test by matching markup.
 */
export interface TitleFocus {
    kind: 'driver' | 'appointment' | 'client' | 'carrier' | 'consignee';
    driverName: string | null;
    appointmentNumber: string | null;
    label: string;
}

/**
 * `PETER LOMBARDO` -> `Peter L.` — the panel is 340px and the surname is not the key.
 *
 * ---- ⛔⛤ THE CASE FIX CAME FROM A TEST, NOT FROM READING IT ------------------
 *
 * ⚠️ Driver names arrive **UPPERCASE** — they are read off a licence scan
 * (`PETER LOMBARDO`, visible on every card). Pete wrote the title as `Pete L.`, and
 * the first version of this function passed the name through untouched and produced
 * `Driver · PETER L.` — which looks like shouting in a heading beside sentence-case
 * everything else.
 *   ** A FUNCTION THAT FORMATS A NAME MUST KNOW WHAT THE DATA ACTUALLY LOOKS LIKE.
 *      I HAD THE SCREENSHOTS AND STILL WROTE IT FOR THE NAME AS TYPED. **
 *
 * ⛔ RE-CASED ONLY WHEN IT IS ALL CAPS. `McDonald` and `O'Brien` are cased
 * deliberately by whoever typed them, and a blanket title-case would flatten them to
 * `Mcdonald` / `O'brien`. Shouting is a signal that nobody chose the case at all.
 *   ** DO NOT RE-CASE WHAT SOMEBODY ALREADY CASED. **
 */
function tidyCase(word: string): string {
    /* ⚠️ `toUpperCase()` comparison, not a regex on A-Z: names carry accents, and
       `ÉTIENNE` is just as shouted as `ETIENNE`. */
    if (word !== word.toUpperCase()) return word;
    return word.charAt(0) + word.slice(1).toLowerCase();
}

export function shortDriverName(full: string | null): string | null {
    const parts = (full ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return null;
    if (parts.length === 1) return tidyCase(parts[0]!);
    /* ⚠️ LAST INITIAL, NOT SECOND. "PETER JOSEPH LOMBARDO" must read "Peter L.", not
       "Peter J." — the surname is what distinguishes two drivers called Peter. */
    return `${tidyCase(parts[0]!)} ${parts[parts.length - 1]!.charAt(0).toUpperCase()}.`;
}

const FOCUS_NOUN: Readonly<Record<TitleFocus['kind'], string>> = {
    driver: 'Driver', appointment: 'Load',
    client: 'Client', carrier: 'Carrier', consignee: 'Consignee',
};

export function panelTitle(subject: PanelSubject, focus: TitleFocus | null): string {
    /* ⛔ NO FOCUS -> THE SUBJECT'S OWN WORD (decision 8). */
    if (!focus) return SUBJECT_WORDS[subject].title;
    const noun = FOCUS_NOUN[focus.kind];
    if (focus.kind === 'driver' || focus.kind === 'appointment') {
        const who = shortDriverName(focus.driverName) ?? focus.label;
        /* ⚠️ THE APPOINTMENT HALF IS OPTIONAL AND OFTEN ABSENT. A walk-up has no load
           yet, and ` / Appt null` would be worse than the driver's name alone. */
        return focus.appointmentNumber
            ? `${noun} · ${who} / Appt ${focus.appointmentNumber}`
            : `${noun} · ${who}`;
    }
    return `${noun} · ${focus.label}`;
}

/**
 * ---- ⭐⭐ DECISION 30: LINE 1 IS THE PANEL, LINE 2 IS THE HOVER (Pete, 2026-09-19) --
 *
 * ⛔⛔ THIS FILLS THE ONE GAP THE BUILD SPEC RECORDED RATHER THAN INVENTED. §1AV.171
 * listed decision 30's party-title strings as missing; Pete chose them on 2026-09-19
 * after reporting BOTH halves of the fault this function fixes:
 *
 *   ⭐ *"it is taking away the - Client line that will have the circle R. when there
 *      is one client, carrier, consignee selected, this should go under that first
 *      line so you still see the client, carrier, consignee name so you can
 *      distinguish"*
 *   ⭐ *"The Title doesn't go back to Driver Check-In when you select the driver
 *      option in the scope"*
 *
 * ⛔ THOSE TWO ASKS CANNOT BOTH BE SATISFIED BY ONE STRING, which is why this is a
 * second function and not a tweak to `panelTitle`. `panelTitle` answers *"what am I
 * pointing at"* and is now LINE 2. This answers *"what panel is this, and whose"* and
 * is LINE 1.
 *   ** A HEADING THAT NAMES THE TRANSIENT THING STOPS NAMING THE PANEL. **
 *
 * ⭐ THE PANEL WORD COMES FIRST AND IS ALWAYS PRESENT, so picking the `Driver` chip
 * returns `Driver Check-Ins` to the front of the line even while a party is still
 * scoped — which is exactly what Pete reported missing. The party is APPENDED, never
 * substituted, so it stays legible at the same time.
 *
 * ⛔ AND IT TAKES THE PARTY SCOPE, NOT THE EFFECTIVE FOCUS. Handing it a hovered load
 * would put `· A00000346 · DD122 · 1:00 PM` in the heading and reintroduce the bug on
 * the other line. The caller is responsible for passing the STANDING scope only.
 */
/**
 * ---- ⭐⭐ THE SELECTED FORM, AND IT IS THE SINGULAR NOUN (Pete, 2026-09-20) --------
 *
 * ⭐ Pete: *"When a driver is checked, Driver Check-Ins changes to Driver - P. Lombardo.
 * Also remove the appointment number next to Driver - Appt # and Make P.Lombardo not
 * bold and uncapitalized font."* · *"Carrier - NAME is not there."*
 *
 * ⛔⚤ ~~`${SUBJECT_WORDS[subject].title} · ${label}`~~ — that gave `Driver Check-Ins ·
 * A00000345 · PETER LOMBARDO`: the PLURAL panel word, the appointment number, and a
 * name in the database's own shouting case. Three faults in one string.
 *
 * ⭐⭐ THE NOUN COMES FROM THE FOCUS, NOT THE SUBJECT, AND THAT IS THE WHOLE FIX. A
 * heading with nothing selected describes a LIST — plural. A heading with one thing
 * selected describes THAT THING — singular. `FOCUS_NOUN` already held both halves.
 *   ** A LABEL THAT DESCRIBES A LIST AND A LABEL THAT DESCRIBES ONE MEMBER OF IT ARE
 *      NOT THE SAME WORD. **
 *
 * ⭐⭐⭐ AND PETE OVERRULED HIS OWN SPELLING WITH A REASON FROM THE DOCK. He wrote
 * `P. Lombardo`; asked, he chose **`Peter L.`** — *"it will most likely save space and
 * the scheduler will call out the first name."* ⚠️ `shortDriverName` already produced
 * exactly that, for a DIFFERENT stated reason (*"the surname is what distinguishes two
 * drivers called Peter"*). **The two arguments disagree and the answer does not**, which
 * is worth knowing before somebody "fixes" one of them.
 *
 * ⛔ NO APPOINTMENT NUMBER HERE. Line 2 (`panelTitle`) still carries it — that line
 * names a LOAD and the number is its identity. This line names a PERSON or a COMPANY.
 */
export function panelHeadline(subject: PanelSubject, party: TitleFocus | null, other: TitleFocus | null = null): string {
    const parts = panelHeadlineParts(subject, party, other);
    return parts.who ? `${parts.word} · ${parts.who}` : parts.word;
}

/**
 * ⭐ THE SAME ANSWER, SPLIT, BECAUSE THE TWO HALVES ARE DRAWN DIFFERENTLY. Pete asked
 * for the name **not bold**, so the panel word and the name cannot be one text node.
 * ⚠️ `panelHeadline` stays as the single string for the `title=` tooltip and for the
 * guards — one computation, two renderings, so they cannot drift.
 *   ** WHEN TWO PARTS OF ONE SENTENCE ARE STYLED DIFFERENTLY, RETURN THE PARTS — NOT
 *      TWO FUNCTIONS THAT MUST AGREE. **
 */
export function panelHeadlineParts(
    subject: PanelSubject, party: TitleFocus | null,
    /**
     * ⭐ D2 / Decision 239 — a scope held from ANOTHER chip (a client scope while the
     * Carrier chip is showing). Named after the list word, never instead of it:
     * `Carriers · for client TRAFFIX`. ⛔ This is what makes holding the scope across
     * chips safe; the 2026-09-20 bug was a foreign scope named as if it were this
     * list's own (`Clients · PETER LOMBARDO`).
     */
    other: TitleFocus | null = null,
): { word: string; who: string | null } {
    if (!party) {
        const who = other
            ? (other.kind === 'driver'
                ? shortDriverName(other.driverName) ?? other.label.trim()
                : other.label.trim())
            : '';
        return other && who && other.kind !== 'appointment'
            ? { word: SUBJECT_WORDS[subject].title, who: `for ${FOCUS_NOUN[other.kind].toLowerCase()} ${who}` }
            : { word: SUBJECT_WORDS[subject].title, who: null };
    }
    /* ⛔ A PARTY WITH NO LABEL ADDS A DANGLING SEPARATOR. `label` is built from the
       master name, then the booking's copy, then the code — so an empty one means the
       focus was constructed wrong, and ` · ` alone would be the only sign of it.
       ⚠️ IT FALLS BACK TO THE PLURAL, not to a bare noun: `Clients` is true and useful,
       `Client` with nothing after it reads as a broken render. */
    const who = party.kind === 'driver'
        ? shortDriverName(party.driverName) ?? party.label.trim()
        : party.label.trim();
    return who
        ? { word: FOCUS_NOUN[party.kind], who }
        : { word: SUBJECT_WORDS[subject].title, who: null };
}

const KEY = (facilityCode: string) => `dock.panelSubject.v1.${facilityCode}`;

function isSubject(v: unknown): v is PanelSubject {
    return v === 'driver' || v === 'client' || v === 'carrier';
}

/**
 * ⚠️ THROWS IN PRIVATE MODE rather than returning null — same note as
 * `readDoorOrder`. Every access is wrapped.
 * ⛔ AND AN UNRECOGNISED STORED VALUE FALLS BACK TO `driver` rather than being
 * trusted. A subject that no longer exists would otherwise render an empty panel
 * with no chip highlighted and nothing saying why.
 */
export function readPanelSubject(facilityCode: string): PanelSubject {
    try {
        const raw = window.localStorage.getItem(KEY(facilityCode));
        return isSubject(raw) ? raw : 'driver';
    } catch {
        return 'driver';
    }
}

export function writePanelSubject(facilityCode: string, subject: PanelSubject): void {
    try {
        window.localStorage.setItem(KEY(facilityCode), subject);
    } catch {
        /* ⚠️ Quota or blocked site data. The choice still holds for this session;
           it simply will not be remembered. Nothing to interrupt anybody with. */
    }
}
